import api from "api";
import { Business, PaginatedResponse } from "types";

// =================================================================================
// User-Facing Public Business Directory API
// =================================================================================

/**
 * Fetch a paginated list of approved businesses.
 * Can be filtered by search, category, and village.
 *
 * @param params - Query parameters (page, search, category, village)
 * @returns Paginated list of approved businesses
 */
export const getBusinesses = async (
  params?: any
): Promise<PaginatedResponse<Business>> => {
  const res = await api.get("/businesses", { params });
  return res.data;
};

/**
 * Fetch a single approved business by its ID.
 *
 * @param id - Business ID
 * @returns Business details
 * @throws 404 if business not found or not approved
 */
export const getBusiness = async (id: number): Promise<Business> => {
  const res = await api.get(`/businesses/${id}`);
  return res.data;
};

// =================================================================================
// Authenticated User's Own Business Management
// =================================================================================

/**
 * Fetch the business listing for the currently authenticated user.
 *
 * @returns User's business details
 * @throws 404 if user has no business listing
 */
export const getMyBusiness = async (): Promise<Business> => {
  const res = await api.get("/user/business");
  return res.data;
};

/**
 * Create a new business listing for the authenticated user.
 * Business status is automatically set to 'pending' for admin approval.
 *
 * @param payload - Business data (name, description, category, phone, email, website, address)
 * @returns Created business
 * @throws 422 if user already has a business listing
 */
export const createMyBusiness = async (
  payload: Partial<Business>
): Promise<Business> => {
  const res = await api.post("/user/business", payload);
  return res.data;
};

/**
 * Update the authenticated user's business listing.
 * Status is reset to 'pending' automatically for re-approval.
 *
 * @param payload - Updated business data
 * @returns Updated business
 * @throws 404 if user has no business listing
 */
export const updateMyBusiness = async (
  payload: Partial<Business>
): Promise<Business> => {
  const res = await api.put("/user/business", payload);
  return res.data;
};

/**
 * Delete the authenticated user's business listing.
 *
 * @throws 404 if user has no business listing
 */
export const deleteMyBusiness = async (): Promise<any> => {
  const res = await api.delete("/user/business");
  return res.data;
};

// =================================================================================
// Admin Business Management API
// =================================================================================

/**
 * Fetch a paginated list of all businesses for admin.
 * Includes pending and rejected listings.
 *
 * @param params - Query parameters (page, search, sort_by, sort_dir)
 * @returns Paginated list of all businesses
 */
export const adminGetBusinesses = async (
  params?: any
): Promise<PaginatedResponse<Business>> => {
  const res = await api.get("/admin/businesses", { params });
  return res.data;
};

/**
 * Get the count of approved businesses.
 *
 * @returns Number of approved businesses
 */
export const getApprovedBusinessesCount = async (): Promise<number> => {
  try {
    const res = await api.get("/admin/businesses", {
      params: { status: "approved", per_page: 1 },
    });
    // Laravel pagination response has total at the top level
    const count = res.data?.total ?? 0;
    return count;
  } catch (error: any) {
    console.error("Error fetching approved businesses count:", error);
    return 0;
  }
};

/**
 * Fetch any single business by its ID for admin viewing.
 *
 * @param id - Business ID
 * @returns Business details
 * @throws 404 if business not found
 */
export const adminGetBusiness = async (id: number): Promise<Business> => {
  const res = await api.get(`/admin/businesses/${id}`);
  return res.data;
};

/**
 * Update a business's details as an admin.
 *
 * @param id - Business ID
 * @param payload - Updated business data
 * @returns Updated business
 */
export const adminUpdateBusiness = async (
  id: number,
  payload: Partial<Business>
): Promise<Business> => {
  const res = await api.put(`/admin/businesses/${id}`, payload);
  return res.data;
};

/**
 * Update the status of a business listing (approve, reject, pending).
 *
 * @param id - Business ID
 * @param status - New status (pending, approved, or rejected)
 * @returns Updated business
 */
export const adminUpdateBusinessStatus = async (
  id: number,
  status: "pending" | "approved" | "rejected"
): Promise<Business> => {
  const res = await api.patch(`/admin/businesses/${id}/status`, { status });
  return res.data;
};

/**
 * Delete a business listing as an admin.
 *
 * @param id - Business ID
 */
export const adminDeleteBusiness = async (id: number): Promise<any> => {
  const res = await api.delete(`/admin/businesses/${id}`);
  return res.data;
};

// Alias functions for convenience (maintains backward compatibility)
export const approveBusiness = (id: number): Promise<Business> =>
  adminUpdateBusinessStatus(id, "approved");

export const rejectBusiness = (id: number): Promise<Business> =>
  adminUpdateBusinessStatus(id, "rejected");

export const deleteBusiness = (id: number): Promise<void> =>
  adminDeleteBusiness(id);

export default {
  // Public
  getBusinesses,
  getBusiness,
  // User
  getMyBusiness,
  createMyBusiness,
  updateMyBusiness,
  deleteMyBusiness,
  // Admin
  adminGetBusinesses,
  adminGetBusiness,
  adminUpdateBusiness,
  adminUpdateBusinessStatus,
  adminDeleteBusiness,
  // Aliases
  approveBusiness,
  rejectBusiness,
  deleteBusiness,
};
